import {QuestionSet} from "./question-set";
import {Question} from "./question";
import {Scale} from "./scale";
import {Utility} from "./utility";

export class Assessment
{
	private _id:string;
	private _uid:string;
	private _name:string;
	private _display_order:number;
	private _enabled:number;
	private _instructions:string;
	private _display_client_results:boolean;
	private _question_sets:QuestionSet[];
	private _scales:Scale[];
	private _version:number;
	private _resourcesURL:string;
	private _creator:string;
	private _creatorUrl:string;
	private _description:string;
	private _therapistDescription:string;
	private _isPublic:boolean;
	
	constructor() {
		this.id = null;
		this.display_order = 1;
		this.enabled = 1;
		this.instructions = "";
		this.display_client_results = true;
		this.question_sets = [];
		this.scales = [];
		this.version = 1;
		// TODO: stephen when we expand to other tests we need to change this.
		this.name = "";
		this.uid = "";
		this.resourcesURL = "";
		this.creator = "";
		this.creatorUrl = "";
		this.description = "";
		this.isPublic = false;
	}
	
	public get creator() : string {
		return this._creator;
	}
	
	public set creator(v:string) {
		this._creator = v;
	}
	
	public get description() : string {
		return this._description;
	}
	
	public set description(v:string) {
		this._description = v;
	}
	
	
	public get creatorUrl() : string {
		return this._creatorUrl;
	}
	
	public set creatorUrl(v:string) {
		this._creatorUrl = v;
	}
	public get resourcesURL() : string {
		return this._resourcesURL;
	}
	
	public set resourcesURL(v : string) {
		this._resourcesURL = v;
	}
	
	public get uid() : string {
		return this._uid;
	}
	
	public set uid(v : string) {
		this._uid = v;
	}
	
	public get name() : string {
		return this._name;
	}
	
	public set name(v : string) {
		this._name = v;
	}
	
	public get id():string {
		return this._id;
	}
	public set id(id:string) {
		this._id = id;
	}
	
	public get display_order():number {
		return this._display_order;
	}
	public set display_order(order:number) {
		this._display_order = order;
	}
	
	public get enabled():number {
		return this._enabled;
	}
	public set enabled(enable:number) {
		this._enabled = enable;
	}
	public get instructions() : string {
		return this._instructions;
	}
	public set instructions(desc:string) {
		this._instructions = desc;
	}
	
	public get display_client_results() : boolean {
		return this._display_client_results;
	}
	public set display_client_results(display:boolean) {
		this._display_client_results = display;
	}
	
	public get question_sets() : QuestionSet[] {
		return this._question_sets;
	}
	public set question_sets(sets:QuestionSet[]) {
		this._question_sets = sets;
	}
	
	public get scales() : Scale[] {
		return this._scales;
	}
	public set scales(scales:Scale[]) {
		this._scales = scales;
	}
	public get version() : number {
		return this._version;
	}
	public set version(version:number) {
		this._version = version;
	}


	public get therapistDescription(): string {
		return this._therapistDescription;
	}

	public set therapistDescription(value: string) {
		this._therapistDescription = value;
	}

	public get isPublic(): boolean {
		return this._isPublic;
	}

	public set isPublic(value: boolean) {
		this._isPublic = value;
	}
	
	addQuestionSet(qs:QuestionSet) {
		this.question_sets.push(qs);
	}
	removeQuestionSet(qs:QuestionSet) {
		let index = this.question_sets.indexOf(qs);
		if (index >= 0) {
			this.question_sets.splice(index,1);
		}
	}
	addScale(scale:Scale) {
		this.scales.push(scale);
	}
	removeScale(scale:Scale) {
		let index = this.scales.indexOf(scale);
		if (index >= 0) {
			this.scales.splice(index,1);
			this.question_sets.forEach(q => q.removeScale(scale));
		}
	}
	
	getQuestionsForScale(scale:Scale) {
		let questions:Question[] = [];
		this.question_sets.forEach(qs => {
			let qsQuestions = qs.questions.filter(q => q.assessment_scale.id === scale.id);
			questions = questions.concat(qsQuestions);
		});
		return questions;
	}

	shiftScaleUp(scale:Scale) {
		Utility.shiftCollectionUp(this._scales, scale);
	}
	shiftScaleDown(scale:Scale) {
		Utility.shiftCollectionDown(this._scales, scale);
	}
	
	shiftQuestionSetUp(questionSet:QuestionSet) {
		Utility.shiftCollectionUp(this._question_sets, questionSet);
	}
	
	shiftQuestionSetDown(questionSet:QuestionSet) {
		Utility.shiftCollectionDown(this._question_sets, questionSet);
	}

	getQuestionSetsForDisplay() : QuestionSet[] {
		return this._question_sets.filter(qs => this.shouldDisplayQuestionSet(qs));
	}
	private shouldDisplayQuestionSet(qs:QuestionSet) { 
		let displayConditions = qs.getDisplayConditions();
		if (!displayConditions || !displayConditions.length) {
			return true;
		}

		let showRatings = displayConditions.map(dc => dc.getDisplay(this));
		// TODO: stephen when we can have multiple conditions we need to have some kind of point ranking
		// or other system for this
		if (showRatings.length) {
			if (showRatings[0] === 'hide') {
				return false;
			}
		}
		return true;
	}


	getFirstQuestionSetToDisplay() {
		let questionSets = this.getQuestionSetsForDisplay();
		if (questionSets.length) {
			return questionSets[0];
		}
		return null;
	}

	getQuestionSetById(id:string) {
		let set = this._question_sets.find(qs => qs.id == id);
		return set;
	}

	getQuestionsListById() {
		let questionsById = (this._question_sets || [])
			.map(qs => qs.questions || []) // grab the questions
			.reduce((prev, curr) => prev.concat(curr), []) // concatenate all the arrays
			.reduce((prev, curr) => { prev[curr.id] = curr; return prev;}, {}); // now return the questions by their ids, every question id is unique
		return questionsById;
	}

	getNextQuestionSetToDisplay(questionSet:QuestionSet) {
		let index = this._question_sets.indexOf(questionSet);
		if (index < 0) {
			throw new Error("The question set could not be found with id " + questionSet.id);
		}

		for (var i = index + 1; i < this._question_sets.length; i++) {
			if (this.shouldDisplayQuestionSet(this._question_sets[i])) {
				return this._question_sets[i];
			}
		}
		return null;
	}
	getPreviousQuestionSetToDisplay(questionSet:QuestionSet) {
		let index = this._question_sets.indexOf(questionSet);
		if (index < 0) {
			throw new Error("The question set could not be found with id " + questionSet.id);
		}

		for (var i = index - 1; i >= 0; i--) {
			if (this.shouldDisplayQuestionSet(this._question_sets[i])) {
				return this._question_sets[i];
			}
		}
		return null;
	}
	getFlaggedQuestions() : Question[] {
		let questions = [];
		let flaggedQuestions = this.question_sets.map(qs => {
			return qs.questions.filter(q => q.isFlaggedForReview);
		})
		.filter(questions => questions.length)
		.reduce((prev, cur) => {
			return cur.concat(prev);
		}, []);
		return flaggedQuestions;
	}
}


// WEBPACK FOOTER //
// ./src/app/shared/models/assessment.ts